import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Grid,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import { REGIONS, getStatesByCountry } from "../../../lib/regions";
import { ArrowBack, CheckCircle, Edit } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { getKyc, submitKyc, updateKyc, getProfileData } from "../../../api/auth";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../layout/DashboardLayout";
import { toast } from "react-toastify";

const steps = ["Agency Info", "Contact Info", "Additional Info", "Review & Confirm"];

const IATA_OPTIONS = ["Pending", "Not Applicable", "Provided"];

const kycSchema = Yup.object().shape({
  agencyInfo: Yup.object().shape({
    name: Yup.string().required("Agency name is required"),
    ownerName: Yup.string().required("Owner name is required"),
    address: Yup.string().required("Address is required"),
    country: Yup.string().required("Country is required"),
    state: Yup.string().required("State / Province is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
  }),
  contactInfo: Yup.object().shape({
    name: Yup.string().required("Contact name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().required("Phone number is required"),
    birthday: Yup.date().typeError("A valid date is required").required("Birthday is required"),
  }),
  additionalInfo: Yup.object().shape({
    businessDocUrl: Yup.string().required("Business document upload is required"),
    iataStatus: Yup.string().required("IATA Status is required"),
    iataNumber: Yup.string().nullable(),
    nantaNumber: Yup.string().nullable(),
    mainPcc: Yup.string().nullable(),
  }),
});

function InfoRow({ label, value }) {
  return (
    <Box display="flex" alignItems="flex-start" py={1} borderBottom="1px solid" borderColor="divider">
      <Typography variant="body2" color="text.secondary" sx={{ width: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}

function KycReadOnlyView({ kyc, onEdit }) {
  const navigate = useNavigate();
  const ai = kyc.additionalInfo || {};

  return (
    <DashboardLayout>
      <Box maxWidth="800px" mx="auto" mt={4} px={2} pb={6}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/dashboard")}
          variant="text"
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircle color="success" sx={{ fontSize: 36 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">KYC Details</Typography>
              <Chip label="Submitted" color="success" size="small" sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Button variant="outlined" startIcon={<Edit />} onClick={onEdit}>
            Edit Details
          </Button>
        </Box>

        <Card sx={{ mb: 3 }} elevation={2}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>Agency Information</Typography>
            <Divider sx={{ mb: 2 }} />
            <InfoRow label="Agency Name" value={kyc.agencyInfo?.name} />
            <InfoRow label="Owner Name" value={kyc.agencyInfo?.ownerName} />
            <InfoRow label="Address" value={kyc.agencyInfo?.address} />
            <InfoRow label="Country" value={kyc.agencyInfo?.country} />
            <InfoRow label="State / Province" value={kyc.agencyInfo?.state} />
            <InfoRow label="Email" value={kyc.agencyInfo?.email} />
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }} elevation={2}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>Contact Information</Typography>
            <Divider sx={{ mb: 2 }} />
            <InfoRow label="Contact Name" value={kyc.contactInfo?.name} />
            <InfoRow label="Email" value={kyc.contactInfo?.email} />
            <InfoRow label="Phone" value={kyc.contactInfo?.phone} />
            <InfoRow
              label="Date of Birth"
              value={
                kyc.contactInfo?.birthday
                  ? new Date(kyc.contactInfo.birthday).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })
                  : null
              }
            />
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>Additional Information</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="flex-start" py={1} borderBottom="1px solid" borderColor="divider">
              <Typography variant="body2" color="text.secondary" sx={{ width: 180, flexShrink: 0 }}>
                Business Document
              </Typography>
              {ai.businessDocUrl ? (
                <a href={ai.businessDocUrl} target="_blank" rel="noopener noreferrer">
                  <Typography variant="body2" fontWeight={500} color="primary">
                    View Document
                  </Typography>
                </a>
              ) : null}
            </Box>
            <InfoRow label="IATA Status" value={ai.iataStatus} />
            {ai.iataNumber && <InfoRow label="IATA Number" value={ai.iataNumber} />}
            {ai.nantaNumber && <InfoRow label="NANTA Number" value={ai.nantaNumber} />}
            {ai.mainPcc && <InfoRow label="Main PCC" value={ai.mainPcc} />}
            {ai.branchPccs?.length > 0 && (
              <InfoRow label="Branch PCCs" value={ai.branchPccs.join(", ")} />
            )}
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
}

export default function KycForm() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [existingKyc, setExistingKyc] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(kycSchema),
    defaultValues: {
      agencyInfo: { name: "", ownerName: "", address: "", country: "", state: "", email: "" },
      contactInfo: { name: "", email: "", phone: "", birthday: "" },
      additionalInfo: { businessDocUrl: "", iataStatus: "", iataNumber: "", nantaNumber: "", mainPcc: "" },
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const kycRes = await getKyc();
        if (kycRes?.data?.kyc) {
          setExistingKyc(kycRes.data.kyc);
          setLoading(false);
          return;
        }
      } catch (_) {
        // No existing KYC — proceed to pre-fill from agency profile
      }

      try {
        const profileRes = await getProfileData();
        const agency = profileRes.data?.agency;
        if (agency) {
          setValue("agencyInfo.name", agency.name || "");
          setValue("agencyInfo.email", agency.email || "");
        }
      } catch (_) {}

      setLoading(false);
    })();
  }, [setValue]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload`, {
        method: "POST",
        body: data,
      });
      const fileData = await res.json();
      if (fileData.url) {
        setValue("additionalInfo.businessDocUrl", fileData.url);
        toast.success("Document uploaded successfully.");
      } else {
        toast.error(fileData.error || "Upload failed. Please try again.");
      }
    } catch {
      toast.error("Something went wrong during upload. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleNext = async () => {
    const sectionKey = ["agencyInfo", "contactInfo", "additionalInfo"][activeStep];
    const valid = await trigger(sectionKey);
    if (valid) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleStartEdit = () => {
    if (existingKyc) {
      const birthday = existingKyc.contactInfo?.birthday
        ? existingKyc.contactInfo.birthday.slice(0, 10)
        : "";
      setValue("agencyInfo", existingKyc.agencyInfo || {});
      setValue("contactInfo", { ...existingKyc.contactInfo, birthday });
      setValue("additionalInfo", existingKyc.additionalInfo || {});
    }
    setActiveStep(0);
    setIsEditing(true);
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (isEditing) {
        await updateKyc(data);
      } else {
        await submitKyc(data);
      }
      navigate("/dashboard");
    } catch {
      toast.error("Error submitting KYC. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (existingKyc && !isEditing) {
    return <KycReadOnlyView kyc={existingKyc} onEdit={handleStartEdit} />;
  }

  const values = getValues();

  return (
    <DashboardLayout>
      <Box maxWidth="800px" mx="auto" mt={4} px={2} pb={6}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/dashboard")}
          variant="text"
          size="small"
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        <Typography variant="h5" fontWeight="bold" mb={0.5}>
          {isEditing ? "Edit Your KYC" : "Complete Your KYC"}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {isEditing
            ? "Update your agency details below."
            : "Help us verify your agency details to unlock full platform access."}
        </Typography>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card elevation={2}>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <form onSubmit={handleSubmit(onSubmit)}>

              {/* Step 1 — Agency Info */}
              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" mb={0.5}>Agency Information</Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Tell us about your travel agency. The name and email are pre-filled from your registration — update them here if needed.
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="agencyInfo.name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Agency Name"
                            fullWidth
                            error={!!errors.agencyInfo?.name}
                            helperText={errors.agencyInfo?.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="agencyInfo.ownerName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Owner Name"
                            fullWidth
                            error={!!errors.agencyInfo?.ownerName}
                            helperText={errors.agencyInfo?.ownerName?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="agencyInfo.address"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Address"
                            fullWidth
                            error={!!errors.agencyInfo?.address}
                            helperText={errors.agencyInfo?.address?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="agencyInfo.country"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.agencyInfo?.country}>
                            <InputLabel>Country</InputLabel>
                            <Select
                              {...field}
                              label="Country"
                              onChange={(e) => {
                                field.onChange(e);
                                setValue("agencyInfo.state", "");
                              }}
                            >
                              {Object.entries(REGIONS).map(([region, countries]) => [
                                <MenuItem key={`hdr-${region}`} disabled sx={{ fontWeight: 700, opacity: 1, color: "#64748b", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1 }}>
                                  {region}
                                </MenuItem>,
                                ...Object.keys(countries).map((c) => (
                                  <MenuItem key={c} value={c} sx={{ pl: 3 }}>{c}</MenuItem>
                                )),
                              ])}
                            </Select>
                            {errors.agencyInfo?.country && (
                              <FormHelperText>{errors.agencyInfo.country.message}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="agencyInfo.state"
                        control={control}
                        render={({ field }) => {
                          const watchedCountry = getValues("agencyInfo.country");
                          const stateOptions = watchedCountry ? getStatesByCountry(watchedCountry) : [];
                          return (
                            <FormControl fullWidth error={!!errors.agencyInfo?.state} disabled={!watchedCountry}>
                              <InputLabel>State / Province / Region</InputLabel>
                              <Select {...field} label="State / Province / Region">
                                {stateOptions.map((s) => (
                                  <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                              </Select>
                              {errors.agencyInfo?.state && (
                                <FormHelperText>{errors.agencyInfo.state.message}</FormHelperText>
                              )}
                            </FormControl>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="agencyInfo.email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Agency Email"
                            fullWidth
                            error={!!errors.agencyInfo?.email}
                            helperText={errors.agencyInfo?.email?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Step 2 — Contact Info */}
              {activeStep === 1 && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" mb={0.5}>Contact Information</Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Primary contact details for your agency.
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="contactInfo.name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Contact Name"
                            fullWidth
                            error={!!errors.contactInfo?.name}
                            helperText={errors.contactInfo?.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="contactInfo.email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Contact Email"
                            fullWidth
                            error={!!errors.contactInfo?.email}
                            helperText={errors.contactInfo?.email?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="contactInfo.phone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Phone Number"
                            fullWidth
                            error={!!errors.contactInfo?.phone}
                            helperText={errors.contactInfo?.phone?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="contactInfo.birthday"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Date of Birth"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.contactInfo?.birthday}
                            helperText={
                              errors.contactInfo?.birthday?.message ||
                              "We celebrate our clients — don't miss the party!"
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Step 3 — Additional Info */}
              {activeStep === 2 && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" mb={0.5}>Additional Information</Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Provide your credentials and business documentation.
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Controller
                        name="additionalInfo.businessDocUrl"
                        control={control}
                        render={({ field }) => (
                          <Box>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                              Business Document
                            </Typography>
                            <Button variant="outlined" component="label" disabled={uploading}>
                              {uploading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                              {uploading ? "Uploading..." : "Upload Business Document"}
                              <input type="file" hidden onChange={handleFileUpload} />
                            </Button>
                            {field.value ? (
                              <Typography variant="body2" mt={1} color="success.main">
                                Uploaded —{" "}
                                <a href={field.value} target="_blank" rel="noopener noreferrer">
                                  View file
                                </a>
                              </Typography>
                            ) : errors.additionalInfo?.businessDocUrl ? (
                              <Typography variant="caption" color="error" display="block" mt={0.5}>
                                {errors.additionalInfo.businessDocUrl.message}
                              </Typography>
                            ) : null}
                          </Box>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="additionalInfo.iataStatus"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.additionalInfo?.iataStatus}>
                            <InputLabel>IATA Status</InputLabel>
                            <Select {...field} label="IATA Status">
                              {IATA_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                            {errors.additionalInfo?.iataStatus && (
                              <FormHelperText>
                                {errors.additionalInfo.iataStatus.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="additionalInfo.iataNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="IATA Number (optional)" fullWidth />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="additionalInfo.nantaNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="NANTA Number (optional)" fullWidth />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="additionalInfo.mainPcc"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Main PCC (optional)" fullWidth />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Step 4 — Review & Confirm */}
              {activeStep === 3 && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" mb={0.5}>Review & Confirm</Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Go through your details carefully before submitting. Once submitted, they will be used to verify your agency.
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    If anything looks wrong, use the <strong>Back</strong> button to correct it before submitting.
                  </Alert>

                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                        Agency Information
                      </Typography>
                      <InfoRow label="Agency Name" value={values.agencyInfo?.name} />
                      <InfoRow label="Owner Name" value={values.agencyInfo?.ownerName} />
                      <InfoRow label="Address" value={values.agencyInfo?.address} />
                      <InfoRow label="Country" value={values.agencyInfo?.country} />
                      <InfoRow label="State / Province" value={values.agencyInfo?.state} />
                      <InfoRow label="Email" value={values.agencyInfo?.email} />
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                        Contact Information
                      </Typography>
                      <InfoRow label="Contact Name" value={values.contactInfo?.name} />
                      <InfoRow label="Email" value={values.contactInfo?.email} />
                      <InfoRow label="Phone" value={values.contactInfo?.phone} />
                      <InfoRow
                        label="Date of Birth"
                        value={
                          values.contactInfo?.birthday
                            ? new Date(values.contactInfo.birthday).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })
                            : null
                        }
                      />
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                        Additional Information
                      </Typography>
                      <InfoRow
                        label="Business Document"
                        value={values.additionalInfo?.businessDocUrl ? "Uploaded" : "Not uploaded"}
                      />
                      <InfoRow label="IATA Status" value={values.additionalInfo?.iataStatus} />
                      {values.additionalInfo?.iataNumber && (
                        <InfoRow label="IATA Number" value={values.additionalInfo.iataNumber} />
                      )}
                      {values.additionalInfo?.nantaNumber && (
                        <InfoRow label="NANTA Number" value={values.additionalInfo.nantaNumber} />
                      )}
                      {values.additionalInfo?.mainPcc && (
                        <InfoRow label="Main PCC" value={values.additionalInfo.mainPcc} />
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Navigation */}
              <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  onClick={activeStep === 0 ? () => navigate("/dashboard") : handleBack}
                  variant="outlined"
                  startIcon={activeStep === 0 ? <ArrowBack /> : undefined}
                >
                  {activeStep === 0 ? "Cancel" : "Back"}
                </Button>

                {activeStep < steps.length - 1 ? (
                  <Button onClick={handleNext} variant="contained" size="large">
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    size="large"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                  >
                    {submitting ? "Saving..." : isEditing ? "Save Changes" : "Confirm & Submit"}
                  </Button>
                )}
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
}
